import { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Sparkles, X } from 'lucide-react';
import { useTopBarLeftContent } from './context/TopBarLeftContentContext';
import { useMobileFilter } from './context/MobileFilterContext';
import { useMobileTopNavBar } from './context/MobileTopNavBarContext';
import CollapsibleFilterGroup from './CollapsibleFilterGroup';
import MobileFilterDrawer from './ui/MobileFilterDrawer';

const platformOptions = [
  { value: 'all', label: 'Todas' },
  { value: 'google', label: 'Google Ads' },
  { value: 'meta', label: 'Meta Ads' },
];

const statusOptions = [
  { value: 'all', label: 'Todos los estados' },
  { value: 'ACTIVE', label: 'Activo' },
  { value: 'PAUSED', label: 'Pausado' },
];

export default function CampaignsFilterBar({
  activePlatform,
  setActivePlatform,
  activeStatus,
  setActiveStatus,
  campaignSearch,
  setCampaignSearch,
  showSearch,
  setShowSearch,
  onCreate,
}) {
  const { registerContent, clearContent } = useTopBarLeftContent();
  const { isMobileFilterOpen, closeMobileFilter } = useMobileFilter();
  const { setTopNavBarConfig } = useMobileTopNavBar();
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  // Track window resize to update responsive layout
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const filtersContent = useMemo(() => {
    const isSmallScreen = windowWidth < 1024; // Mobile (<768px) + Tablet (768-1023px) both use drawer

    if (isSmallScreen) {
      // Filter icon now in MobileTopNavBar
      return null;
    }

    // Desktop version: Keep original layout
    return (
    <div
      className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-xl p-3"
      data-testid="campaigns-sticky-filter"
    >
      <div className="flex flex-wrap items-center gap-3">
        <CollapsibleFilterGroup
          label="Plataforma"
          value={activePlatform}
          options={platformOptions}
          onChange={setActivePlatform}
        />
        <CollapsibleFilterGroup
          label="Estado"
          value={activeStatus}
          options={statusOptions}
          onChange={setActiveStatus}
        />
        {showSearch && (
          <div className="flex-1 flex items-center gap-2 min-w-[200px]">
            <Search className="w-4 h-4 text-white/70 flex-shrink-0" />
            <input
              type="text"
              placeholder="Buscar por nombre..."
              value={campaignSearch}
              onChange={(e) => setCampaignSearch(e.target.value)}
              onBlur={() => {
                if (!campaignSearch) setShowSearch(false);
              }}
              className="flex-1 bg-white/10 border border-white/20 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-fuchsia-400 placeholder:text-white/40 min-w-0"
              autoFocus
            />
            <button
              type="button"
              onClick={() => {
                setCampaignSearch('');
                setShowSearch(false);
              }}
              className="p-1 text-white/70 hover:text-white transition flex-shrink-0"
              title="Cerrar búsqueda"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {!showSearch && (
          <button
            type="button"
            onClick={() => setShowSearch(true)}
            className="flex items-center gap-2 text-white/70 hover:text-white transition"
            title="Buscar"
          >
            <Search className="w-4 h-4" />
          </button>
        )}
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          <button
            type="button"
            onClick={onCreate}
            className="flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-fuchsia-500 to-blue-600 text-white text-sm font-medium hover:from-fuchsia-600 hover:to-blue-700 transition shadow-lg hover:shadow-xl hover:scale-105 duration-300"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Crear campaña
            <Sparkles className="w-4 h-4 ml-1.5" />
          </button>
        </div>
      </div>
    </div>
    );
  }, [
    windowWidth,
    activePlatform,
    activeStatus,
    campaignSearch,
    showSearch,
    setActivePlatform,
    setActiveStatus,
    setCampaignSearch,
    setShowSearch,
    onCreate,
  ]);

  useEffect(() => {
    registerContent(filtersContent);
    return () => clearContent();
  }, [filtersContent, registerContent, clearContent]);

  const isSmallScreen = windowWidth < 1024; // Mobile (<768px) + Tablet (768-1023px) both use drawer

  useEffect(() => {
    if (!isSmallScreen) {
      setTopNavBarConfig({ filterAdjacentContent: null });
      return;
    }
    setTopNavBarConfig({
      filterAdjacentContent: (
        <button
          type="button"
          onClick={onCreate}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-gradient-to-r from-fuchsia-500 to-blue-600 text-white text-xs font-medium hover:from-fuchsia-600 hover:to-blue-700 transition shadow-md active:scale-95 touch-manipulation whitespace-nowrap"
        >
          <Plus className="w-3.5 h-3.5 shrink-0" />
          Crear campaña
          <Sparkles className="w-3 h-3 shrink-0 opacity-90" />
        </button>
      ),
    });
    return () => setTopNavBarConfig({ filterAdjacentContent: null });
  }, [isSmallScreen, onCreate, setTopNavBarConfig]);

  return (
    <>
      {/* Mobile filter drawer - shows on mobile and small screens */}
      {isSmallScreen && (
        <MobileFilterDrawer
          isOpen={isMobileFilterOpen}
          onClose={closeMobileFilter}
          filters={[
            {
              key: 'platform',
              label: 'Plataforma',
              options: platformOptions
            },
            {
              key: 'status',
              label: 'Estado',
              options: statusOptions
            }
          ]}
          selectedFilters={{
            platform: activePlatform,
            status: activeStatus
          }}
          onFilterChange={(key, value) => {
            if (key === 'platform') setActivePlatform(value);
            else if (key === 'status') setActiveStatus(value);
          }}
          darkMode={true}
        />
      )}
    </>
  );
}
